/* eslint-disable react-hooks/set-state-in-effect */
/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, X, Image, Cpu, Search, ArrowLeft, Save, Stethoscope, CalendarDays, Euro } from 'lucide-react';
import logger from '@/lib/logger';
import { TagInput } from '@/components/TagInput';
import { PageTitle } from '@/components/backoffice/PageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];

const EMPTY_FORM = {
  nome: '', cognome: '', specializzazione: '', email: '', telefono: '', bio: '', foto_url: '',
  servizi_ids: [], tariffe: [], availability: [],
  mostra_disponibilita: true, mostra_tariffe: false, durata_slot_minuti: 30, attrezzature_richieste: [],
  anticipo_tipo: 'nessuno', anticipo_percentuale: 30
};

export default function DottoreEdit() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const isNew = !doctorId || doctorId === 'nuovo';

  const [form, setForm] = useState(EMPTY_FORM);
  const [services, setServices] = useState([]);
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [doctorName, setDoctorName] = useState('');
  // Service picker
  const [servicePickerOpen, setServicePickerOpen] = useState(false);
  const [serviceSearch, setServiceSearch] = useState('');
  const [pickerCategory, setPickerCategory] = useState('doctor');
  // Media picker
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaSearch, setMediaSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const sRes = await axios.get(`${API}/services?active_only=false&include_prices=true`);
      setServices(sRes.data);
      try {
        const tRes = await axios.get(`${API}/equipment-tags`);
        setTagSuggestions(tRes.data.tags || []);
      } catch (e) { logger.warn('Tag suggestions non caricati:', e?.message); }
      if (isNew) {
        setForm({ ...EMPTY_FORM, availability: [0, 1, 2, 3, 4].map(d => ({ day_of_week: d, start_time: '09:00', end_time: '18:00' })) });
      } else {
        const dRes = await axios.get(`${API}/doctors/${doctorId}`);
        const doc = dRes.data;
        setDoctorName(`${doc.nome} ${doc.cognome}`);
        setForm({
          nome: doc.nome, cognome: doc.cognome, specializzazione: doc.specializzazione,
          email: doc.email || '', telefono: doc.telefono || '', bio: doc.bio || '', foto_url: doc.foto_url || '',
          servizi_ids: doc.servizi_ids || [], tariffe: doc.tariffe || [],
          mostra_disponibilita: doc.mostra_disponibilita !== false,
          mostra_tariffe: doc.mostra_tariffe === true,
          durata_slot_minuti: doc.durata_slot_minuti || 30,
          attrezzature_richieste: doc.attrezzature_richieste || [],
          anticipo_tipo: doc.anticipo_tipo || 'nessuno',
          anticipo_percentuale: doc.anticipo_percentuale || 30,
          availability: doc.availability || []
        });
      }
    } catch (err) {
      logger.error('Load error:', err);
      toast.error('Dottore non trovato');
      navigate('/backoffice/dottori');
    } finally { setLoading(false); }
  }, [doctorId, isNew, navigate]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.nome.trim() || !form.cognome.trim()) { toast.error('Nome e cognome sono obbligatori'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tariffe: form.tariffe.map(t => ({ service_id: t.service_id, nome_servizio: t.nome_servizio || '', prezzo: parseFloat(t.prezzo) || 0 })),
        servizi_ids: form.tariffe.map(t => t.service_id),
        durata_slot_minuti: Math.max(5, Math.min(240, parseInt(form.durata_slot_minuti, 10) || 30)),
        anticipo_percentuale: form.anticipo_tipo === 'percentuale' ? Math.max(1, Math.min(100, parseFloat(form.anticipo_percentuale) || 30)) : null
      };
      if (isNew) {
        await axios.post(`${API}/doctors`, { ...payload, active: true }, {});
        toast.success('Dottore creato');
      } else {
        await axios.put(`${API}/doctors/${doctorId}`, payload, {});
        toast.success('Dottore aggiornato');
      }
      navigate('/backoffice/dottori');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally { setSaving(false); }
  };

  // ── Servizi e Tariffe ──
  const doctorCategories = [...new Set(form.tariffe.map(t => {
    const svc = services.find(s => s.id === t.service_id);
    return svc?.categoria;
  }).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'it'));

  const allCategories = [...new Set(services.filter(s => s.active !== false).map(s => s.categoria))].filter(Boolean).sort((a, b) => a.localeCompare(b, 'it'));

  const openServicePicker = () => {
    setServiceSearch('');
    // Pre-filtra per le categorie dei servizi già offerti dal medico
    setPickerCategory(doctorCategories.length > 0 ? 'doctor' : 'all');
    setServicePickerOpen(true);
  };

  const addService = (serviceId) => {
    setForm(prev => {
      if (prev.tariffe.find(t => t.service_id === serviceId)) return prev;
      const svc = services.find(s => s.id === serviceId);
      return { ...prev, tariffe: [...prev.tariffe, { service_id: serviceId, nome_servizio: svc?.nome || '', prezzo: 0 }] };
    });
  };

  const removeService = (serviceId) => {
    setForm(prev => ({ ...prev, tariffe: prev.tariffe.filter(t => t.service_id !== serviceId) }));
  };

  const updateTariffa = (serviceId, prezzo) => {
    setForm(prev => ({
      ...prev,
      tariffe: prev.tariffe.map(t => t.service_id === serviceId ? { ...t, prezzo: parseFloat(prezzo) || 0 } : t)
    }));
  };

  // ── Disponibilità ──
  const toggleDay = (dayIndex) => {
    setForm(prev => {
      const exists = prev.availability.find(a => a.day_of_week === dayIndex);
      if (exists) return { ...prev, availability: prev.availability.filter(a => a.day_of_week !== dayIndex) };
      return { ...prev, availability: [...prev.availability, { day_of_week: dayIndex, start_time: '09:00', end_time: '18:00' }] };
    });
  };

  const updateDayTime = (dayIndex, field, value) => {
    setForm(prev => ({ ...prev, availability: prev.availability.map(a => a.day_of_week === dayIndex ? { ...a, [field]: value } : a) }));
  };

  // ── Media picker ──
  const openMediaPicker = async () => {
    try {
      const res = await axios.get(`${API}/media`, { params: { folder: 'dottori' } });
      setMediaFiles(res.data);
    } catch { setMediaFiles([]); }
    setMediaSearch(""); setMediaPickerOpen(true);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64 rounded" />
        <div className="grid lg:grid-cols-2 gap-6">
          <Skeleton className="h-96 rounded-xl" />
          <Skeleton className="h-96 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="doctor-edit-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/backoffice/dottori')} data-testid="doctor-edit-back-btn">
            <ArrowLeft size={16} className="mr-1" /> Dottori
          </Button>
          <div>
            <PageTitle title={isNew ? 'Nuovo Dottore' : `Modifica — Dott. ${doctorName}`} info={isNew ? 'Compila la scheda del nuovo medico: dati, specializzazione, servizi e tariffe, foto.' : 'Scheda completa del medico: dati, servizi e tariffe, foto, account collegato.'} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/backoffice/dottori')}>Annulla</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#238A92] hover:bg-[#0F5E66] text-white" data-testid="doctor-edit-save-btn">
            <Save size={15} className="mr-1" /> {saving ? 'Salvataggio...' : (isNew ? 'Crea Dottore' : 'Salva Modifiche')}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        {/* Colonna sinistra: Anagrafica */}
        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily:'Space Grotesk'}}><Stethoscope size={18} /> Anagrafica</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Nome</Label><Input value={form.nome} onChange={e => setForm(p => ({...p, nome: e.target.value}))} data-testid="doctor-nome-input" /></div>
                <div className="space-y-2"><Label>Cognome</Label><Input value={form.cognome} onChange={e => setForm(p => ({...p, cognome: e.target.value}))} data-testid="doctor-cognome-input" /></div>
              </div>
              <div className="space-y-2"><Label>Specializzazione</Label><Input value={form.specializzazione} onChange={e => setForm(p => ({...p, specializzazione: e.target.value}))} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Durata slot visita (min)</Label>
                  <Input type="number" min={5} max={240} step={5} value={form.durata_slot_minuti} onChange={e => setForm(p => ({...p, durata_slot_minuti: e.target.value}))} data-testid="doctor-slot-duration-input" />
                </div>
                <div className="space-y-2">
                  <Label>Telefono / WhatsApp</Label>
                  <Input type="tel" value={form.telefono || ''} onChange={e => setForm(p => ({...p, telefono: e.target.value}))} placeholder="+39 333 1234567" data-testid="doctor-telefono-input" />
                </div>
              </div>
              <div className="space-y-2"><Label>Email (per notifiche appuntamenti)</Label><Input type="email" value={form.email || ''} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="dottore@email.com" /></div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5"><Cpu size={14} className="text-[#238A92]" /> Attrezzature richieste</Label>
                <TagInput value={form.attrezzature_richieste} onChange={(tags) => setForm(p => ({...p, attrezzature_richieste: tags}))} suggestions={tagSuggestions} placeholder="es. ECG, Ecografo..." testId="doctor-attrezzature-input" />
                <p className="text-xs text-muted-foreground">Il sistema suggerirà gli Studi che hanno tutta la strumentazione richiesta.</p>
              </div>
              <div className="space-y-2"><Label>Bio</Label><Textarea value={form.bio} onChange={e => setForm(p => ({...p, bio: e.target.value}))} rows={4} /></div>
              <div className="space-y-2">
                <Label>Foto</Label>
                <div className="flex gap-2">
                  <Input value={form.foto_url} onChange={e => setForm(p => ({...p, foto_url: e.target.value}))} placeholder="URL immagine o seleziona dai media" className="flex-1" />
                  <Button type="button" variant="outline" onClick={openMediaPicker}><Image size={14} className="mr-1" /> Media</Button>
                </div>
                {form.foto_url && (
                  <div className="flex items-center gap-3 mt-2">
                    <img src={form.foto_url} alt="Anteprima" className="w-16 h-16 rounded-lg object-cover border" onError={e => { e.target.style.display = 'none'; }} />
                    <Button type="button" variant="ghost" size="sm" className="text-red-500 h-7" onClick={() => setForm(p => ({...p, foto_url: ''}))}><X size={12} className="mr-1" /> Rimuovi</Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg" style={{fontFamily:'Space Grotesk'}}>Visibilità sul sito pubblico</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Mostra disponibilità orari</p>
                  <p className="text-xs text-muted-foreground">I giorni e gli orari saranno visibili nella pagina Equipe</p>
                </div>
                <Checkbox checked={form.mostra_disponibilita} onCheckedChange={v => setForm(p => ({...p, mostra_disponibilita: v}))} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Mostra tariffe</p>
                  <p className="text-xs text-muted-foreground">I prezzi dei servizi saranno visibili ai pazienti registrati</p>
                </div>
                <Checkbox checked={form.mostra_tariffe} onCheckedChange={v => setForm(p => ({...p, mostra_tariffe: v}))} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Colonna destra: Servizi e Disponibilità */}
        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily:'Space Grotesk'}}><Euro size={18} /> Servizi e Tariffe</CardTitle>
                <Button type="button" size="sm" variant="outline" className="h-8 text-[#238A92] border-[#238A92]/40 hover:bg-[#238A92]/10" onClick={openServicePicker} data-testid="doctor-add-service-btn">
                  <Plus size={14} className="mr-1" /> Aggiungi
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-xs text-muted-foreground">Sono elencati solo i servizi offerti da questo dottore. Con tariffa <strong>0</strong> viene applicato il prezzo di listino del catalogo servizi.</p>
              {form.tariffe.length === 0 ? (
                <div className="border rounded-lg py-8 text-center" data-testid="doctor-services-empty">
                  <p className="text-sm text-muted-foreground mb-2">Nessun servizio associato a questo dottore</p>
                  <Button type="button" size="sm" variant="outline" onClick={openServicePicker}>
                    <Plus size={14} className="mr-1" /> Aggiungi il primo servizio
                  </Button>
                </div>
              ) : (
                <div className="border rounded-lg max-h-96 overflow-y-auto" data-testid="doctor-services-list">
                  {form.tariffe.map(t => {
                    const svc = services.find(s => s.id === t.service_id);
                    const listino = svc?.prezzo || 0;
                    const custom = parseFloat(t.prezzo) || 0;
                    return (
                      <div key={t.service_id} className="flex items-center gap-3 px-3 py-2 border-b last:border-b-0" data-testid={`doctor-service-row-${t.service_id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{svc?.nome || t.nome_servizio}</p>
                          <p className="text-[10px] text-muted-foreground">
                            {svc?.categoria || ''}{listino > 0 ? ` — listino ${listino.toFixed(2)} EUR` : ' — nessun prezzo di listino'}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Input type="number" step="0.01" min="0" value={t.prezzo ?? 0} onChange={e => updateTariffa(t.service_id, e.target.value)} className="w-24 h-7 text-sm" data-testid={`doctor-tariffa-input-${t.service_id}`} />
                          <span className="text-xs text-muted-foreground">EUR</span>
                        </div>
                        <span className={`text-[10px] w-24 text-right flex-shrink-0 ${custom > 0 ? 'text-[#1E7A58] font-medium' : 'text-muted-foreground'}`}>
                          {custom > 0 ? 'personalizzata' : (listino > 0 ? `listino ${listino.toFixed(2)}` : 'da definire')}
                        </span>
                        <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-red-600 flex-shrink-0" onClick={() => removeService(t.service_id)} title="Rimuovi servizio" data-testid={`doctor-remove-service-${t.service_id}`}>
                          <Trash2 size={13} />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
              <p className="text-xs text-muted-foreground">{form.tariffe.length} {form.tariffe.length === 1 ? 'servizio offerto' : 'servizi offerti'}</p>

              {/* Anticipo richiesto alla prenotazione */}
              <div className="border-t pt-3 mt-2 space-y-2">
                <p className="text-sm font-medium">Anticipo richiesto alla prenotazione online</p>
                <p className="text-xs text-muted-foreground">L'importo si calcola sul prezzo del servizio (tariffa personalizzata del medico, altrimenti listino). Viene richiesto solo ai pazienti con la richiesta anticipo attiva.</p>
                <div className="flex items-center gap-3">
                  <Select value={form.anticipo_tipo} onValueChange={v => setForm(p => ({...p, anticipo_tipo: v}))}>
                    <SelectTrigger className="w-56" data-testid="doctor-anticipo-tipo-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nessuno">Nessun anticipo</SelectItem>
                      <SelectItem value="totale">Anticipo totale (100%)</SelectItem>
                      <SelectItem value="percentuale">Percentuale del prezzo</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.anticipo_tipo === 'percentuale' && (
                    <div className="flex items-center gap-1">
                      <Input type="number" min="1" max="100" step="1" value={form.anticipo_percentuale} onChange={e => setForm(p => ({...p, anticipo_percentuale: e.target.value}))} className="w-20 h-9" data-testid="doctor-anticipo-percentuale-input" />
                      <span className="text-sm text-muted-foreground">%</span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily:'Space Grotesk'}}><CalendarDays size={18} /> Disponibilità Settimanale</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {DAYS.map((day, i) => {
                  const avail = form.availability.find(a => a.day_of_week === i);
                  return (
                    <div key={`avail-${i}`} className="flex items-center gap-3">
                      <label className="flex items-center gap-2 w-28 text-sm cursor-pointer">
                        <Checkbox checked={!!avail} onCheckedChange={() => toggleDay(i)} />
                        {day}
                      </label>
                      {avail && (
                        <>
                          <Input type="time" value={avail.start_time} onChange={e => updateDayTime(i, 'start_time', e.target.value)} className="w-28 h-8" />
                          <span className="text-sm">-</span>
                          <Input type="time" value={avail.end_time} onChange={e => updateDayTime(i, 'end_time', e.target.value)} className="w-28 h-8" />
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Save in fondo per comodità */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => navigate('/backoffice/dottori')}>Annulla</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#238A92] hover:bg-[#0F5E66] text-white" data-testid="doctor-edit-save-btn-bottom">
              <Save size={15} className="mr-1" /> {saving ? 'Salvataggio...' : (isNew ? 'Crea Dottore' : 'Salva Modifiche')}
            </Button>
          </div>
        </div>
      </div>

      {/* Service Picker Dialog */}
      <Dialog open={servicePickerOpen} onOpenChange={setServicePickerOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle style={{fontFamily:'Space Grotesk'}}>Aggiungi Servizi al Dottore</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">Clicca su un servizio per aggiungerlo. Verrà inserito con tariffa <strong>0</strong> (= prezzo di listino), poi potrai personalizzare l'importo.</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={serviceSearch} onChange={e => setServiceSearch(e.target.value)} placeholder="Cerca per nome o tipologia..." className="pl-8" data-testid="service-picker-search" />
            </div>
            <Select value={pickerCategory} onValueChange={setPickerCategory}>
              <SelectTrigger className="w-full sm:w-52" data-testid="service-picker-category-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {doctorCategories.length > 0 && <SelectItem value="doctor">Tipologie del medico</SelectItem>}
                <SelectItem value="all">Tutte le tipologie</SelectItem>
                {allCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {pickerCategory === 'doctor' && doctorCategories.length > 0 && (
            <p className="text-[11px] text-muted-foreground -mt-1" data-testid="service-picker-doctor-categories-hint">
              Filtro attivo: {doctorCategories.join(', ')}
            </p>
          )}
          <div className="border rounded-lg overflow-y-auto flex-1 min-h-40" data-testid="service-picker-list">
            {(() => {
              const q = serviceSearch.trim().toLowerCase();
              const available = services
                .filter(s => s.active !== false)
                .filter(s => !form.tariffe.find(t => t.service_id === s.id))
                .filter(s => {
                  if (pickerCategory === 'all') return true;
                  if (pickerCategory === 'doctor') return doctorCategories.includes(s.categoria);
                  return s.categoria === pickerCategory;
                })
                .filter(s => !q || (s.nome || '').toLowerCase().includes(q) || (s.categoria || '').toLowerCase().includes(q))
                .sort((a, b) => (a.categoria || '').localeCompare(b.categoria || '', 'it') || (a.nome || '').localeCompare(b.nome || '', 'it'));
              if (available.length === 0) {
                return (
                  <div className="py-8 text-center space-y-2">
                    <p className="text-sm text-muted-foreground">{q ? 'Nessun servizio trovato' : 'Nessun altro servizio disponibile con questo filtro'}</p>
                    {pickerCategory !== 'all' && (
                      <Button type="button" size="sm" variant="outline" onClick={() => setPickerCategory('all')}>Mostra tutte le tipologie</Button>
                    )}
                  </div>
                );
              }
              return available.map(s => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => addService(s.id)}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2 border-b last:border-b-0 text-left hover:bg-[hsl(var(--secondary))] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#238A92]"
                  data-testid={`service-picker-item-${s.id}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{s.nome}</p>
                    <p className="text-[10px] text-muted-foreground">{s.categoria}{s.prezzo ? ` — listino ${s.prezzo.toFixed(2)} EUR` : ''}</p>
                  </div>
                  <Plus size={14} className="text-[#238A92] flex-shrink-0" />
                </button>
              ));
            })()}
          </div>
          <DialogFooter>
            <p className="text-xs text-muted-foreground mr-auto self-center">{form.tariffe.length} {form.tariffe.length === 1 ? 'servizio offerto' : 'servizi offerti'}</p>
            <Button onClick={() => setServicePickerOpen(false)} className="bg-[#238A92] hover:bg-[#0F5E66] text-white" data-testid="service-picker-done">Fatto</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Media Picker Dialog */}
      <Dialog open={mediaPickerOpen} onOpenChange={setMediaPickerOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{fontFamily:'Space Grotesk'}}>Seleziona Foto dalla cartella "Dottori"</DialogTitle>
          </DialogHeader>
          {mediaFiles.length > 0 && (
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={mediaSearch} onChange={e => setMediaSearch(e.target.value)} placeholder="Cerca per nome file (es. cognome)..." className="pl-8 h-9" autoFocus data-testid="doctor-media-search" />
            </div>
          )}
          {mediaFiles.length === 0 ? (
            <div className="text-center py-8">
              <Image size={40} className="mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground mb-2">Nessuna immagine nella cartella "Dottori"</p>
              <p className="text-xs text-muted-foreground">Vai su <strong>Media Library → Dottori</strong> per caricare le foto</p>
            </div>
          ) : (
            (() => {
              const mq = mediaSearch.trim().toLowerCase();
              const list = mq ? mediaFiles.filter(m => (m.original_name || '').toLowerCase().includes(mq) || (m.filename || '').toLowerCase().includes(mq)) : mediaFiles;
              return list.length === 0 ? (
                <p className="text-center text-sm text-muted-foreground py-8" data-testid="doctor-media-empty">Nessuna foto contiene "{mediaSearch.trim()}"</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {list.map(m => (
                    <button
                      key={m.id}
                      type="button"
                      title={m.original_name}
                      onClick={() => { setForm(p => ({...p, foto_url: m.url})); setMediaPickerOpen(false); toast.success('Foto selezionata'); }}
                      className={`relative group aspect-square rounded-lg overflow-hidden border-2 transition-all hover:border-[#238A92] hover:shadow-md ${form.foto_url === m.url ? 'border-[#238A92] ring-2 ring-[#238A92]/30' : 'border-transparent'}`}
                    >
                      <img src={m.url} alt={m.original_name} className="w-full h-full object-cover" />
                      <div className="absolute inset-x-0 bottom-0 bg-black/70 text-white text-[9px] px-1 py-0.5 truncate opacity-0 group-hover:opacity-100 transition-opacity">{m.original_name}</div>
                    </button>
                  ))}
                </div>
              );
            })()
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMediaPickerOpen(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
